import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Seo from '@/components/Seo';
import { requestAccountDeletion } from '@/lib/api';
import { toast } from 'sonner';
import { Trash2, Mail, ShieldCheck, Clock, CheckCircle2 } from 'lucide-react';

const SUPPORT_EMAIL = 'contact@bharat.estate';

const AccountDeletionPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and the email linked to your account');
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestAccountDeletion(form);
      setDone(res);
      toast.success('Deletion request submitted');
    } catch (err) {
      toast.error(err.message || 'Could not submit. Please email us instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Account Deletion Request — Bharat Estate')}&body=${encodeURIComponent('Please delete my Bharat Estate account and associated data.\n\nName:\nAccount email:\nPhone (optional):\nReason (optional):')}`;

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="account-deletion-page">
      <Seo
        title="Delete Your Bharat Estate Account"
        description="Request permanent deletion of your Bharat Estate account and personal data. Submit the form below or email contact@bharat.estate — no app reinstall or login required."
        path="/account-deletion"
      />

      {/* Header */}
      <section className="bg-[#0F172A] text-white">
        <div className="container-luxury py-14">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-5">
            <Trash2 className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-sm text-white/80">Account & Data Removal</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="deletion-heading">
            Delete Your Bharat Estate Account
          </h1>
          <p className="text-white/80 text-base md:text-lg mt-4 max-w-2xl">
            You can permanently delete your Bharat Estate account and the personal data associated
            with it. Choose either option below — no app reinstall and no login is required to make
            the request.
          </p>
        </div>
      </section>

      <div className="container-luxury py-12 grid lg:grid-cols-2 gap-10">
        {/* Left: explanation */}
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">How to request deletion</h2>
          <div className="space-y-5">
            <div className="flex gap-3">
              <div className="mt-1 h-8 w-8 flex-shrink-0 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-[#0F172A] font-bold">1</div>
              <div>
                <p className="font-semibold text-[#0F172A]">Submit the request form</p>
                <p className="text-muted-foreground text-sm">Fill in the short form on this page with the email address linked to your account. This works even if you can no longer sign in.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="mt-1 h-8 w-8 flex-shrink-0 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-[#0F172A] font-bold">2</div>
              <div>
                <p className="font-semibold text-[#0F172A]">Or email our support team</p>
                <p className="text-muted-foreground text-sm">
                  Write to <a href={mailtoHref} className="text-[#0F172A] underline hover:text-[#D4AF37]" data-testid="support-email-link">{SUPPORT_EMAIL}</a> from your registered email with the subject “Account Deletion Request”.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="mt-1 h-8 w-8 flex-shrink-0 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-[#0F172A] font-bold">3</div>
              <div>
                <p className="font-semibold text-[#0F172A]">Signed in? Delete from your profile</p>
                <p className="text-muted-foreground text-sm">If you can log in, go to <Link to="/profile" className="text-[#0F172A] underline hover:text-[#D4AF37]">Profile</Link> and use “Delete my account”, or simply use the form here.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 space-y-4 bg-white border border-border/60 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-[#D4AF37] mt-0.5" />
              <p className="text-sm text-[#0F172A]"><span className="font-semibold">Timeline:</span> requests are processed and your account permanently deleted within <span className="font-semibold">30 days</span>. You’ll get an email confirmation.</p>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-[#D4AF37] mt-0.5" />
              <div className="text-sm text-[#0F172A]">
                <span className="font-semibold">What gets deleted:</span> your profile, saved properties, enquiries, messages and any listings you created.
                <br />
                <span className="text-muted-foreground">We may retain limited records (e.g., invoices/payment records) only where required by law, and never use them for any other purpose.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form / success */}
        <div>
          {done ? (
            <div className="bg-white border border-border/60 rounded-2xl p-8 text-center" data-testid="deletion-success">
              <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Request received</h2>
              <p className="text-muted-foreground mb-4">
                Your account deletion request has been recorded. Reference ID:
              </p>
              <p className="font-mono text-lg font-bold text-[#0F172A] mb-4" data-testid="deletion-reference">{done.request_id}</p>
              <p className="text-sm text-muted-foreground">
                We’ll permanently delete your account within 30 days and email a confirmation to <span className="font-medium text-[#0F172A]">{form.email}</span>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-border/60 rounded-2xl p-6 md:p-8" data-testid="deletion-form">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-1">Request account deletion</h2>
              <p className="text-muted-foreground text-sm mb-6">We’ll use these details to verify and remove your account.</p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="del-name">Full name *</Label>
                  <Input id="del-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" className="mt-1.5" data-testid="deletion-name" />
                </div>
                <div>
                  <Label htmlFor="del-email">Account email *</Label>
                  <Input id="del-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="the email linked to your account" className="mt-1.5" data-testid="deletion-email" />
                </div>
                <div>
                  <Label htmlFor="del-phone">Phone (optional)</Label>
                  <Input id="del-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Registered phone number" className="mt-1.5" data-testid="deletion-phone" />
                </div>
                <div>
                  <Label htmlFor="del-reason">Reason (optional)</Label>
                  <Textarea id="del-reason" value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="Let us know why you're leaving (optional)" className="mt-1.5" rows={3} data-testid="deletion-reason" />
                </div>
              </div>

              <Button type="submit" disabled={submitting} className="w-full mt-6 bg-[#0F172A] hover:bg-[#1e293b] text-white py-6 text-base" data-testid="deletion-submit">
                <Trash2 className="h-4 w-4 mr-2" />
                {submitting ? 'Submitting…' : 'Request Account Deletion'}
              </Button>

              <a href={mailtoHref} className="mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-[#0F172A]" data-testid="deletion-email-alt">
                <Mail className="h-4 w-4" /> Prefer email? Write to {SUPPORT_EMAIL}
              </a>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDeletionPage;
